import { searchDimmingOpacity } from '../calendarSearchModel'
import { useCalendarSearchHighlight } from '../CalendarSearchHighlightContext'
import { Divider, Tag, Tooltip } from 'antd'
import { RightOutlined, EnvironmentOutlined, SyncOutlined, BranchesOutlined, ClockCircleOutlined } from '@ant-design/icons'
import { CalendarEventVisit, DayViewEvent as Event, Timesheet, Visit, VisitWorkareaStatusRecord } from "../scheduleApi"
import { useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import DayViewEventAvatar from './DayViewEventAvatar'
import JobDrawer from '../JobDrawer/JobDrawer'
import { VisitWorkareaStatuses } from '../../../common/constants'
import WorkAreaStatusTag from './WorkAreaStatusTag'
import { EventAddressModal } from './EventAddressModal'
import { getGeocoding } from '../../RouteManagement/Mappers/mapbox'
import { useUpdateJobPropertyGeolocationMutation } from '../../RouteManagement/routingQueries'
import { getVisitForEvent, isEventClockedIn, getDayDateRange, mapDayViewEventToScheduledEvent, areWorkAreasCompleteByIds } from '../scheduleModel'
import { isMultiDayEvent } from './ReorderView/reorderDayViewModel'
import { useSchedule } from '../ScheduleContext'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCommentDots } from '@fortawesome/pro-regular-svg-icons/faCommentDots'
import { faSnowflake } from '@fortawesome/pro-solid-svg-icons'
import { useGetVisitAttachmentsQuery } from '../scheduleQueries'
import { findCrewMemberByEmail } from '../crewModel'

type Props = {
  event: Event
  workareaStatuses: VisitWorkareaStatusRecord[]
  timesheets: Timesheet[]
  visits: Visit[]
  calendarEventVisits: CalendarEventVisit[]
  date: Dayjs
  isHovered: boolean
  onMouseEnter: () => void
  onMouseLeave: () => void
}

type NotesPhotosIndicatorProps = {
  visit: Visit | undefined
}

const NotesPhotosIndicator = ({ visit }: NotesPhotosIndicatorProps) => {
  const { data: attachments } = useGetVisitAttachmentsQuery(visit?.id)

  const hasNotes = visit?.internalNotes ? true : false
  const hasPhotos = (attachments?.filter(a => a.thumbnail)?.length ?? 0) > 0

  if (!hasNotes && !hasPhotos) {
    return null
  }

  return (
    <Tooltip title="Notes & Photos">
      <FontAwesomeIcon icon={faCommentDots} style={{ fontSize: '18px', color: '#999' }} />
    </Tooltip>
  )
}

const DayViewEvent = ({ event, workareaStatuses, timesheets, visits, calendarEventVisits, date, isHovered, onMouseEnter, onMouseLeave }: Props) => {
  const { searchTerm: highlightTerm } = useCalendarSearchHighlight()
  const { crew } = useSchedule()
  const visit = getVisitForEvent(event, calendarEventVisits, visits, getDayDateRange(date))
  const isCurrent = isEventClockedIn(event, calendarEventVisits, visits, timesheets, crew ?? [], getDayDateRange(date))

  const visitWorkareaIds = visit
    ? workareaStatuses
      .filter(ws => ws.visitId === visit.id)
      .map(ws => ws.workareaId)
    : []

  const eventWorkareaIds = (event.workAreaIds && event.workAreaIds !== '')
    ? event.workAreaIds.split(',').map(id => parseInt(id.trim()))
    : []

  const visitTimesheets = visit ? timesheets.filter(t => t.timecardId === visit.id) : []

  const timesheetWorkareaIds = visitTimesheets
    .filter(t => t.projectWorkareaId)
    .map(t => t.projectWorkareaId as number)

  const effectiveWorkareaIds = visit
    ? [...new Set([...visitWorkareaIds, ...timesheetWorkareaIds])]
    : [...new Set(eventWorkareaIds)]

  const hasWorkAreas = effectiveWorkareaIds.length > 0
  const visitWorkareaStatuses = visit ? workareaStatuses.filter(ws => ws.visitId === visit.id) : []
  const areAllComplete = areWorkAreasCompleteByIds(eventWorkareaIds, visitWorkareaStatuses)

  const actualStartTime = visitTimesheets.length > 0
    ? dayjs.utc(visitTimesheets.sort((a, b) => dayjs(a.startDateUtc).valueOf() - dayjs(b.startDateUtc).valueOf())[0].startDateUtc).local()
    : null
  const actualEndTime = visitTimesheets.length > 0
    ? (() => {
      const sorted = visitTimesheets.sort((a, b) => dayjs(b.endDateUtc).valueOf() - dayjs(a.endDateUtc).valueOf())
      const endDate = sorted[0].endDateUtc
      return endDate ? dayjs.utc(endDate).local() : null
    })()
    : null

  const eventData = {
    event: mapDayViewEventToScheduledEvent(event),
    calendarEventVisits,
    visits,
    visitWorkareaStatuses: workareaStatuses,
    timesheets,
  }

  const [jobDrawerOpen, setJobDrawerOpen] = useState(false)
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false)
  const { mutate: updateJobPropertyGeolocation, isLoading: isUpdatingJobPropertyGeolocation } = useUpdateJobPropertyGeolocationMutation()

  const onJobDrawerClose = () => {
    setJobDrawerOpen(false)
  }

  const foremanColor = () => {
    if (event.crewForemanEmail === null) {
      return null
    }

    const crewLead = findCrewMemberByEmail(crew, event.crewForemanEmail)
    if (!crewLead) {
      return null
    }

    return crewLead.color || '#000000'
  }

  const hasMissingCoordinates = event.latitude === null || event.longitude === null

  return (
    <>
      <div
        style={{
          margin: '4px 16px',
          padding: '10px 12px',
          backgroundColor: 'white',
          borderRadius: '8px',
          borderTop: hasMissingCoordinates && isHovered ? '2px solid #ff4d4f' : '1px solid #e8e8e8',
          borderRight: hasMissingCoordinates && isHovered ? '2px solid #ff4d4f' : '1px solid #e8e8e8',
          borderBottom: hasMissingCoordinates && isHovered ? '2px solid #ff4d4f' : '1px solid #e8e8e8',
          borderLeft: foremanColor() ? `8px solid ${foremanColor()}` : (hasMissingCoordinates && isHovered ? '2px solid #ff4d4f' : '1px solid #e8e8e8'),
          cursor: 'pointer',
          boxShadow: isHovered ? (hasMissingCoordinates ? '0 0 8px 1px rgba(255, 77, 79, 0.4)' : '0 0 8px 1px rgba(250, 173, 20, 0.3)') : 'none',
          transition: 'box-shadow 0.2s, transform 0.2s, border 0.2s',
          transform: isHovered ? 'scale(1.02)' : 'scale(1)',
          opacity: searchDimmingOpacity(event, highlightTerm),
        }}
        onClick={() => setJobDrawerOpen(true)}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
          <div style={{ fontSize: '15px', fontWeight: '600', color: '#000', textDecoration: areAllComplete ? 'line-through' : undefined }}>
            {event.jobName}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <NotesPhotosIndicator visit={visit ?? undefined} />
            <Tooltip title={hasMissingCoordinates ? "Click to resolve" : "Change Address"}>
              <EnvironmentOutlined
                style={{ color: hasMissingCoordinates ? '#ff4d4f' : '#999', fontSize: '16px', cursor: isUpdatingJobPropertyGeolocation ? 'wait' : 'pointer' }}
                onClick={async (e) => {
                  e.stopPropagation()
                  if (isUpdatingJobPropertyGeolocation) return

                  if (hasMissingCoordinates) {
                    const addressString = [
                      event.customerAddress1,
                      event.customerCity,
                      event.customerState,
                      event.customerZip
                    ].filter(Boolean).join(' ')

                    if (addressString.trim()) {
                      const result = await getGeocoding(addressString)
                      if (result && event.propertyId) {
                        const context = result.properties.context || {}
                        const address = context.address?.name || result.properties.address_line1 || ''
                        const city = context.place?.name || result.properties.context_city || ''
                        const state = context.region?.region_code || result.properties.context_region || ''
                        const zip = context.postcode?.name || result.properties.context_postcode || ''
                        const county = context.district?.name || ''
                        const country = context.country?.country_code || 'US'

                        updateJobPropertyGeolocation({
                          propertyId: event.propertyId,
                          nickname: '',
                          address1: address,
                          address2: null,
                          city: city,
                          state: state,
                          zip: zip,
                          county: county,
                          countryCode: country,
                          latitude: result.geometry.coordinates[1],
                          longitude: result.geometry.coordinates[0]
                        })
                      } else {
                        setIsAddressModalOpen(true)
                      }
                    } else {
                      setIsAddressModalOpen(true)
                    }
                  } else {
                    setIsAddressModalOpen(true)
                  }
                }}
              />
            </Tooltip>
            {event.routeId && (
              <Tooltip title={event.routeName || "Scheduled from Route"}>
                <BranchesOutlined style={{ color: '#999', fontSize: '14px' }} />
              </Tooltip>
            )}
            {event.repeatingId && (
              <Tooltip title="Repeating Event">
                <SyncOutlined style={{ color: '#999', fontSize: '14px' }} />
              </Tooltip>
            )}
            {event.frozen && (
              <Tooltip title="Frozen — cannot be rescheduled">
                <FontAwesomeIcon icon={faSnowflake} style={{ color: '#999', fontSize: '14px' }} />
              </Tooltip>
            )}
            {!isCurrent && <DayViewEventAvatar event={event} visit={visit} />}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '12px', color: '#999', textDecoration: areAllComplete ? 'line-through' : undefined }}>
            {event.customerDisplayName || 'Customer Name'}
          </div>
          {(isMultiDayEvent(event) || event.allDay) && !isCurrent && !visit ? (
            <span style={{ display: 'flex', gap: '4px' }}>
              {isMultiDayEvent(event) && (
                <Tooltip title={event.allDay
                  ? `${dayjs(event.start).format('MMM D, YYYY')} - ${dayjs(event.end).format('MMM D, YYYY')}`
                  : `${dayjs(event.start).format('MMM D, YYYY h:mm A')} - ${dayjs(event.end).format('MMM D, YYYY h:mm A')}`
                }>
                  <Tag style={{ fontSize: '11px', color: '#000' }}>Multi Day</Tag>
                </Tooltip>
              )}
              {event.allDay && (
                <Tooltip title="This event was created with the All Day setting.">
                  <Tag style={{ fontSize: '11px', color: '#000' }}>All Day</Tag>
                </Tooltip>
              )}
            </span>
          ) : (
            <span style={{ fontSize: '14px', color: '#666' }}>
              {(actualStartTime || dayjs(event.start)).format('h:mm A')} - {isCurrent ? 'Now' : (actualEndTime || dayjs(event.end)).format('h:mm A')}
            </span>
          )}
        </div>

        <Divider style={{ margin: '8px 0' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {isCurrent ? (
            <DayViewEventAvatar event={event} visit={visit} />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#666', fontSize: '14px' }}>
                {hasWorkAreas
                  ? `${effectiveWorkareaIds.length} Work Area${effectiveWorkareaIds.length === 1 ? '' : 's'}`
                  : 'No Assigned Work Areas'
                }
              </span>
              {hasWorkAreas && (
                <>
                  <WorkAreaStatusTag
                    visit={visit}
                    workareaStatuses={workareaStatuses}
                    statusNumber={VisitWorkareaStatuses.InProgress}
                    effectiveWorkareaIds={effectiveWorkareaIds}
                  />
                  <WorkAreaStatusTag
                    visit={visit}
                    workareaStatuses={workareaStatuses}
                    statusNumber={VisitWorkareaStatuses.Completed}
                    effectiveWorkareaIds={effectiveWorkareaIds}
                  />
                  <WorkAreaStatusTag
                    visit={visit}
                    workareaStatuses={workareaStatuses}
                    statusNumber={VisitWorkareaStatuses.Skipped}
                    effectiveWorkareaIds={effectiveWorkareaIds}
                  />
                  <WorkAreaStatusTag
                    visit={visit}
                    workareaStatuses={workareaStatuses}
                    statusNumber={VisitWorkareaStatuses.SkippedReschedule}
                    effectiveWorkareaIds={effectiveWorkareaIds}
                  />
                </>
              )}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isCurrent && (
              <span style={{ color: '#fa8c16', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                Active Timer
                <ClockCircleOutlined style={{ fontSize: '16px' }} />
              </span>
            )}
            <div
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '8px',
                border: '1px solid #d9d9d9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <RightOutlined style={{ color: '#999', fontSize: '12px' }} />
            </div>
          </div>
        </div>
      </div>

      <JobDrawer
        open={jobDrawerOpen}
        onClose={onJobDrawerClose}
        eventData={eventData}
        date={date}
        dateRange={getDayDateRange(date)}
      />

      <EventAddressModal
        isOpen={isAddressModalOpen}
        onClose={() => setIsAddressModalOpen(false)}
        event={event}
      />
    </>
  )
}

export default DayViewEvent